/**
 * City 审图「多轮整改（correction）」区块 —— Planning Review / Building Review 两个阶段专用。
 *
 * 需求来源（2026-10-20 用户原话，配合手绘截图）：
 *   「在 Planning 审流程和 Building 审流程中将 city 改为多次的，因为可能会涉及到政府退回后
 *    重新做……那么这两个阶段下方就增加一个按钮，上一步对应的提交是否通过，如果已经通过
 *    就直接下一步，如果没有通过就在这两个阶段中增加对应的 correction 列……每一个 correction
 *    列需要有人去上传 1. 图纸 2. response letter 这两样东西……然后整体项目进度延期 3 天，
 *    每增加一次延期项目整体进度 3 天……还需要在配置中增加这个 correction 的责任人的配置」
 *
 * 为什么单独成块（而不是塞进现有子任务或产物卡）：
 *   审图退回是**按轮次**发生的，一轮 = 一次 City 意见 + 一份修改图 + 一份回复函。
 *   现有子任务卡是「一次性待办」，产物卡是「一个产物一个状态」，两者都表达不了
 *   「第 3 轮在等图纸、第 2 轮已交齐」这种并存的多个历史状态。
 *
 * 交互主流程：
 *   ① 块顶先回答「上一次提交是否通过」——这是用户明确要求的那个按钮：
 *        · 通过 → 调 pass 接口，关阶段并激活下一阶段（等同「关闭阶段」，带全部门禁）；
 *        · 未通过 → 新增一条 correction 轮次，同时把项目整体顺延 N 天（默认 3，可配置）；
 *   ② 每条轮次卡里是两个**固定槽位**：修改图纸 / 回复函。
 *      分开的原因是判定「这一轮还缺什么」必须能逐类判断，混成一堆附件就判不出来了。
 *   ③ 两类都交齐 → 「标记完成（已重新提交）」；之后才能再开下一轮或判定通过。
 *
 * 2026-10-21 补充需求（用户原话）：
 *   「两个审流程 现在 审未通过需要附件...通过也需要附件」
 *   → 块顶先有「City 审查结论附件」区：通过 / 未通过**两个按钮都要求**至少一份
 *     未作废的结论附件（plan check 结果单 / 批准函）。后端在 pass 与新增轮次两个
 *     接口里各有一道同口径校验，前端只按 count 控制按钮可点，不重复实现规则。
 *     附件存在独立集合（design_correction_results），不与轮次的图纸 / 回复函混存：
 *     后者是我方交给 City 的，前者是 City 给我方的结果，混在一起会让缺件判断失真。
 *
 * 只读 vs 可编辑：写操作要求 meta.canEdit（设计部成员 + 项目归属，由后端二次校验）。
 *
 * 2026-10-23 补充需求（用户原话「这个位置请允许可以拖拽文件上传」+ 截图圈出本块）：
 *   本块原来三个上传入口（City 审查结论附件 / 每轮的修改图纸 / 每轮的回复函）都只能点按钮
 *   走系统文件框；用户在桌面端已经有「把文件从资源管理器拖进页面」的习惯，
 *   拖进来时浏览器默认会**直接打开该文件并覆盖当前页面**，把一个上传动作变成「丢掉正在看的项目」。
 *   现在三个投放区都支持拖拽（整行 / 整卡都是热区，不用先找准 28px 的按钮），
 *   并在块根节点兜底拦掉「拖偏了落在空白处」的默认行为。
 *
 * 2026-10-24 补充需求（反馈 FB5abc7cbd3d）：
 *   项目已经进入 Building 审图后，仍要能补传此前 Planning 审图阶段的 correction。
 *   已关闭阶段允许补 City 结论附件并新增“历史整改轮次”，轮次仍按图纸 + 回复函
 *   两槽位闭环；历史补录只写工作记录，不回退阶段、不改变截止日或项目工期。
 *
 * 2026-10-27 补充需求（用户反馈，306 Jackson St 截图）：
 *   「City Review Result(Correction Round) 栏目前仅支持 1 Round 添加文件，请修改为每一轮
 *    都可以添加文件，因为每一类 City 审核意见都不相同……Correction 轮数和上传文件窗口数
 *    应保持一致。如有 3 轮上传，则应该对应有 3 个不同的 City Review Result」。
 *
 *   改前：结论附件按**阶段**存（后端 document 是 {projectId, stageKey, files[]}，条目里没有
 *   轮次字段），整段审图只有一个上传窗。线上真实代价：306 Jackson St 里 R1- / R2- 共 14 份
 *   文件挤在同一列表，只能靠用户自己在文件名前打前缀区分；10-23 Iris 提前传的第二轮资料
 *   被整批作废，原因原文「第二轮资料误提前上传；应完成第一轮 Correction 后再录入第二轮」。
 *
 *   改后：**每轮整改卡内各有一个「City 审查意见」上传窗**，轮数 = 窗口数；
 *   阶段级窗口保留，只承载「未归轮」的文件（初次送审意见 / 批准函 / 改造前的存量文件），
 *   并给存量文件提供人工归轮入口（后端 assign-round）。
 *
 *   为什么保留阶段级窗口而不是全部下沉到轮次：
 *     ① 「通过」时传的是批准函，那是**整个阶段**的结论，不属于任何一轮整改；
 *     ② 改造前已有 18 个阶段文档、几十份存量附件，没有轮次标记，直接删窗口会让它们
 *        在界面上凭空消失（历史证据不能丢，这是项目红线）。
 *   为什么判定门禁仍按「未作废总数」：通过 / 未通过要求的是「本阶段有没有 City 书面凭证」，
 *     与它挂在哪一轮无关。改成按轮计数会让老文件解锁不了按钮，用户会以为功能坏了。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Ban, CheckCircle2, ChevronDown, ChevronRight, Clock, Download, FileCheck2, FileUp, History, Loader2, Lock, RotateCcw, Send, ShieldCheck, UploadCloud, XCircle } from "lucide-react";
import { toast } from "../../../components/ui/sonner";
import { MediaLightbox, mediaKind } from "../../../components/MediaLightbox";
import { downloadFile } from "../../../lib/fileDownload";
import { DESIGN_UPLOAD_ACCEPT } from "./meta";
import { T } from "../../../lib/i18n";
import { fmtDT } from "../../../lib/usDate";
import { errTextOf } from "../../../lib/detailText";
import { DesignConfirmDialog } from "./DesignConfirmDialog";
import FileUploadDate, { uploadDateLabel } from "./FileUploadDate";
/* 【2026-10-30 输入侧时区提示】City 回复期限按美国日历日解释（顺延也按美国工作日算），
   中国同事按北京日期填会让整改期限差一天。 */
import { UsDateHint } from "./UsDateHint";

const SLOT_META = {
  drawing: { label: "修改图纸", hint: "Revise plans / drawings", icon: FileUp, tone: "violet" },
  response_letter: { label: "回复函（Response Letter）", hint: "Point-by-point response to City", icon: Send, tone: "sky" },
};

const fmtMB = (n) => `${((n || 0) / 1048576).toFixed(1)} MB`;

/* 单文件上限与后端 design_corrections.MAX_FILE 对齐（60MB）。
   前端先拦一道只为给出即时提示，权威校验仍在后端 —— 拖拽时用户看不到「选完才发现太大」。 */
const MAX_UPLOAD_MB = 60;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

/* 兜底拦截：只对「文件」拖拽生效，页面内拖文字 / 拖元素不受影响。
   放在模块级而不是 hook 里，是因为它不需要 state，块根节点会反复用到。 */
const hasFilesEvent = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
const blockFileDrop = (e) => { if (hasFilesEvent(e)) e.preventDefault(); };

/* 拖拽投放手势（2026-10-23 用户需求「这个位置请允许可以拖拽文件上传」）。
   三个投放区（结论附件 / 修改图纸 / 回复函）共用这一份，避免各写一遍后行为不一致。

   要点与原因：
   ① 只认「文件」拖拽（dataTransfer.types 含 Files）：页面内拖动一段文字 / 一张图片
      也会触发 drag 事件，不筛的话会把选中文本当成上传来源。
   ② 用 depth 计数而不是布尔值：拖过子元素（文件名、图标）会连续触发 dragleave，
      布尔值会让高亮疯狂闪烁，计数归零才真正算「离开」。
   ③ 即使禁用（无权限 / 本轮已完成 / 正在上传）也必须 preventDefault：
      不拦的话浏览器默认行为是「直接打开这个文件」，用户当前正在看的项目页会被顶掉。
   ④ 松手后一次性把 FileList 交给调用方，由调用方按顺序逐个调接口（后端一次收一个文件），
      保证「拖 3 张图」和「点上传选 3 张图」的结果完全一致。 */
const useFileDrop = ({ onFiles, disabled }) => {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const hasFiles = hasFilesEvent;
  const reset = () => { depth.current = 0; setDragging(false); };
  const props = {
    onDragEnter: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      if (!disabled) setDragging(true);
    },
    onDragOver: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = disabled ? "none" : "copy";
    },
    onDragLeave: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      reset();
      if (disabled) return;
      const list = Array.from(e.dataTransfer.files || []);
      if (list.length) onFiles(list);
    },
  };
  return { dragging, props };
};

/* 一轮的状态药丸。三个状态互斥，颜色沿用设计部既有语义：
   open = 缺材料（红）、ready = 交齐待重交（琥珀）、closed = 已重交 / 历史已归档（蓝）。
   刻意不用绿色：绿色在这个系统里只表示「已通过审核」，而整改完成 ≠ City 批准。 */
const RoundBadge = ({ r }) => {
  // 作废的轮次排在最前：它既没关闭也不该报「待补齐 N 项」—— 报缺件会让人以为还要补材料。
  if (r.voidedAt) return <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600" data-testid={`dv3-cr-badge-${r.id}`} data-state="voided">{T("已作废（误开）")}</span>;
  if (r.closedAt) return <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700" data-testid={`dv3-cr-badge-${r.id}`} data-state="closed">{r.retrospective ? T("历史记录已归档") : T("已重新提交")}</span>;
  if (r.complete) return <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`dv3-cr-badge-${r.id}`} data-state="ready">{r.retrospective ? T("历史材料已交齐 · 待归档") : T("材料已交齐 · 待重新提交")}</span>;
  return <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700" data-testid={`dv3-cr-badge-${r.id}`} data-state="open">{T("待补齐 {0} 项", [r.missing?.length || 0])}</span>;
};

/* 单个槽位：文件名列表 + 上传按钮 + 缺件提示。
   一个槽位可以有多份文件（例如图纸分 A-100 / A-101 两张），所以用列表而不是单文件字段。

   投放区（2026-10-23 用户需求「这个位置请允许可以拖拽文件上传」）：
   整块槽位（标题行 + 文件名列表）都是热区，不只那个 28px 的「上传」按钮 ——
   拖拽时鼠标往往落在卡片中间，要求用户对准按钮等于没做拖拽。
   高亮用虚线描边 + 浅底，和项目里 ProjectFiles / SharedResources 的投放反馈同一套语义。 */
const Slot = ({ r, slot, meta, busy, onUpload, onVoid, onPreview }) => {
  const S = SLOT_META[slot];
  const Icon = S.icon;
  const files = (r.files || []).filter((f) => f.slot === slot);
  const live = files.filter((f) => !f.voided);
  const inp = useRef(null);
  const tone = live.length ? "text-slate-700" : "text-red-600";
  const isBusy = busy === `${r.id}-${slot}`;
  /* 本轮已标记完成（closedAt）或没有编辑权限时不再是投放区：
     后端这两种情况都会 400 拒绝，前端提前把投放关掉并仍拦下浏览器默认打开行为。 */
  const canUpload = !!meta.canEdit && !r.closedAt;
  const { dragging, props: dropProps } = useFileDrop({
    disabled: !canUpload || isBusy,
    onFiles: (list) => onUpload(r, slot, list),
  });
  return (
    <div className={`rounded-md border px-2 py-1.5 transition-colors ${dragging ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200" : "border-slate-200 bg-white"}`}
      data-testid={`dv3-cr-slot-${r.id}-${slot}`} data-slot={slot} data-have={live.length ? "1" : "0"}
      data-dragging={dragging ? "1" : "0"} {...dropProps}>
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3 w-3 shrink-0 ${live.length ? "text-slate-400" : "text-red-500"}`} />
        <span className={`text-[11px] font-semibold ${tone}`}>{T(S.label)}</span>
        {!live.length && <span className="text-[10px] text-red-500 font-semibold">{T("未上传")}</span>}
        {canUpload && (
          <>
            <button type="button" title={T("上传{0}（也可直接把文件拖到这里）", [T(S.label)])} disabled={isBusy}
              data-testid={`dv3-cr-upload-${r.id}-${slot}`} onClick={() => inp.current?.click()}
              className="ml-auto inline-flex items-center gap-0.5 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 disabled:opacity-50">
              {isBusy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <FileUp className="h-2.5 w-2.5" />}{T("上传")}
            </button>
            {/* 多选：拖拽可以一次拖多份，点选也要能一次选多份，否则同一个槽位两种入口产出不同结果 */}
            <input ref={inp} type="file" multiple className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
              data-testid={`dv3-cr-upload-input-${r.id}-${slot}`}
              onChange={(e) => { const list = Array.from(e.target.files || []); e.target.value = ""; if (list.length) onUpload(r, slot, list); }} />
          </>
        )}
      </div>
      {dragging && (
        <div className="mt-1 flex items-center justify-center gap-1 rounded border-2 border-dashed border-violet-400 bg-white/70 py-1.5 text-[10px] font-semibold text-violet-700 pointer-events-none"
          data-testid={`dv3-cr-drop-hint-${r.id}-${slot}`}>
          <UploadCloud className="h-3 w-3" />{T("松开即上传到「{0}」（可多份）", [T(S.label)])}
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-1 space-y-0.5 pl-4" data-testid={`dv3-cr-files-${r.id}-${slot}`}>
          {files.map((f) => (
            <li key={f.aid} className="flex items-center gap-1 text-[10px]" data-testid={`dv3-cr-file-${f.aid}`} data-voided={f.voided ? "1" : "0"}>
              <div className="min-w-0 flex-1 basis-[180px]">
                <button type="button" onClick={() => onPreview(r, f)} title={T("页内预览")}
                  className={`block w-full truncate text-left font-semibold ${f.voided ? "text-slate-400 line-through" : "text-violet-700 hover:underline"}`}>
                  {f.name}
                </button>
                <FileUploadDate value={f.uploadedAt} fallbackValue={f.at} className="block" />
              </div>
              <span className="shrink-0 text-slate-400">{fmtMB(f.size)}</span>
              <button type="button" title={T("下载")} onClick={() => downloadFile(f.fileId, f.name).catch((e) => toast.error(e?.message || T("下载失败")))}
                className="shrink-0 text-slate-400 hover:text-violet-700"><Download className="h-2.5 w-2.5" /></button>
              {meta.canEdit && !r.closedAt && !f.voided && (
                <button type="button" title={T("作废（保留留档）")} data-testid={`dv3-cr-void-${f.aid}`} onClick={() => onVoid(r, f)}
                  className="shrink-0 text-slate-400 hover:text-red-600"><XCircle className="h-2.5 w-2.5" /></button>
              )}
              {f.voided && <span className="shrink-0 text-red-500" title={f.voidReason}>{T("已作废")}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* 每轮结论附件的投放区容器（2026-10-27）。
   为什么要单独包一层组件：拖拽手势 `useFileDrop` 是 hook，而轮次是 `rounds.map` 渲染的，
   hook 不能写在循环里（轮数一变，hooks 顺序就变，React 会直接报错）。
   包成组件后每一轮各自拥有一个独立的 hook 实例，轮数增减都安全。
   children 用函数形式传回 (dragging, dropProps)，保持与阶段级投放区完全一致的视觉与手势。 */
const EvidenceDropArea = ({ r, disabled, onFiles, children }) => {
  const { dragging, props } = useFileDrop({
    disabled,
    onFiles: (list) => onFiles(r, list),
  });
  return children(dragging, props);
};

/* 单轮整改卡：轮次头（第 N 轮 / 状态 / 时间 / 操作人）→ City 意见 → 本轮 City 审查意见附件
   → 两个槽位 → 完成按钮。

   2026-10-27：新增「本轮 City 审查意见」上传窗（用户需求「每一轮都可以添加文件……
   轮数和上传文件窗口数应保持一致」）。它和「图纸 / 回复函」两个槽位**分开一段渲染**，
   不并进那两格的 grid：那两个槽位是「我方交给 City」的材料，这一份是「City 给我们的」，
   混在同一排会让人以为三类材料缺一不可，而实际上图纸和回复函才是本轮完成的必要条件
   （后端 _slot_ok 只校验这两个槽位，结论附件不参与本轮 complete 判定）。
   位置放在两个槽位**上面**：City 的意见是这一轮要改什么的前提，先摆因由再摆交付。 */
const RoundCard = ({ r, meta, busy, onUpload, onVoid, onPreview, onClose, onVoidRound,
                     evidenceFiles = [], evidenceBusy = null,
                     onEvidenceUpload = null, onEvidenceVoid = null, onEvidencePreview = null }) => {
  const [open, setOpen] = useState(!r.closedAt);
  useEffect(() => { if (!r.closedAt) setOpen(true); }, [r.closedAt]);
  const parties = r.parties || {};
  const who = (slot) => parties[slot]?.name || "—";
  const evLive = evidenceFiles.filter((f) => !f.voided);
  const evInp = useRef(null);
  /* 本轮已关闭仍允许补 City 意见：City 的意见常常比我们的整改动作晚到，
     轮次关闭后再补传真发生过（历史补录场景），拒绝上传只会逼用户去伪造文件名。
     是否可传与阶段级窗口同一口径：有编辑权限即可。 */
  const canUploadEv = !!meta.canEdit;
  /* 本轮的忙碌标记：上传时是 `ev-round-N`，作废某份时是 `aid-void`。
     两个 key 分开判断 —— 混在一起会让「正在作废第 1 轮的文件」把第 2 轮的上传按钮也禁掉。 */
  const evUploading = evidenceBusy === `ev-round-${r.round}`;
  return (
    <div className={`rounded-lg border ${r.voidedAt ? "border-slate-200 bg-slate-100/70" : r.closedAt ? "border-slate-200 bg-slate-50/60" : r.complete ? "border-amber-200 bg-amber-50/30" : "border-red-200 bg-red-50/20"}`}
      data-testid={`dv3-cr-round-${r.id}`} data-round={r.round} data-state={r.state}
      data-voided={r.voidedAt ? "1" : "0"}>
      <div className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer" onClick={() => setOpen((v) => !v)} data-testid={`dv3-cr-toggle-${r.id}`}>
        {open ? <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" /> : <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />}
        <span className="shrink-0 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold text-white">{T("第 {0} 轮", [r.round])}</span>
        <RoundBadge r={r} />
        {r.retrospective && (
          <span className="shrink-0 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold text-amber-700"
            data-testid={`dv3-cr-history-${r.id}`}><History className="mr-0.5 inline h-2.5 w-2.5" />{T("历史补录")}</span>
        )}
        <span className="min-w-0 flex-1 truncate text-[11px] text-slate-600" title={r.reason}>{r.reason}</span>
        {/* 轮次头上直接把「本轮有几份 City 意见」标出来：不展开也能判断该轮有没有凭证，
            这正是用户说的「每一类 City 审核意见都不相同」要对齐的东西。 */}
        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${evLive.length ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}`}
          data-testid={`dv3-cr-evcount-${r.id}`} data-count={evLive.length}
          title={T("本轮 City 审查意见附件 {0} 份", [evLive.length])}>
          {T("City 意见 {0}", [evLive.length])}
        </span>
        <span className="shrink-0 text-[10px] text-slate-400 hidden sm:inline">{r.openedBy} · {fmtDT(r.openedAt)}</span>
      </div>
      {open && (
        <div className="space-y-1.5 border-t border-slate-200/70 px-2 pb-2 pt-1.5">
          {/* City 意见全文：截断显示会让「到底要改哪一条」不可读，所以给足行数并允许换行 */}
          <p className="whitespace-pre-wrap rounded bg-white/70 px-1.5 py-1 text-[11px] leading-5 text-slate-700" data-testid={`dv3-cr-reason-${r.id}`}>{r.reason}</p>
          {/* 本轮 City 审查意见附件（2026-10-27 用户需求）。复用 Slot 同款投放区手势与样式，
              但**不复用 Slot 组件**：Slot 绑的是「轮次 document 内 files[] + slot 参数」，
              而结论附件存在独立集合、还带轮次字段，硬塞进 Slot 会让两条数据源混在一起。
              投放区用 EvidenceDropArea 包一层：hook 不能写在 rounds.map 里，否则轮数一变
              hooks 顺序就变。 */}
          <EvidenceDropArea r={r} disabled={!canUploadEv || evUploading}
            onFiles={(rr, list) => onEvidenceUpload?.(rr, list)}>
            {(dragging, dropProps) => (
          <div className={`rounded-md border px-2 py-1.5 transition-colors ${dragging ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200" : "border-slate-200 bg-white"}`}
            data-testid={`dv3-cr-evidence-${r.id}`} data-count={evLive.length}
            data-have={evLive.length ? "1" : "0"} data-dragging={dragging ? "1" : "0"}
            {...dropProps}>
            <div className="flex flex-wrap items-center gap-1.5">
              <FileCheck2 className="h-3 w-3 shrink-0 text-slate-400" />
              <span className="text-[11px] font-semibold text-slate-700">{T("第 {0} 轮 City 审查意见", [r.round])}</span>
              {!evLive.length
                ? <span className="text-[10px] text-slate-400" data-testid={`dv3-cr-evidence-empty-${r.id}`}>{T("可选 · 这一轮 City 给的退回意见 / 结果单")}</span>
                : <span className="text-[10px] text-slate-400">{T("{0} 份", [evLive.length])}</span>}
              {canUploadEv && (
                <>
                  <button type="button" onClick={() => evInp.current?.click()}
                    disabled={evUploading}
                    title={T("上传第 {0} 轮 City 审查意见（也可直接把文件拖到这里）", [r.round])}
                    data-testid={`dv3-cr-evidence-upload-${r.id}`}
                    className="ml-auto inline-flex items-center gap-0.5 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 disabled:opacity-50">
                    {evUploading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <FileUp className="h-2.5 w-2.5" />}{T("上传")}
                  </button>
                  <input ref={evInp} type="file" multiple className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
                    data-testid={`dv3-cr-evidence-input-${r.id}`}
                    onChange={(e) => { const list = Array.from(e.target.files || []); e.target.value = ""; if (list.length) onEvidenceUpload?.(r, list); }} />
                </>
              )}
            </div>
            {dragging && (
              <div className="mt-1 flex items-center justify-center gap-1 rounded border-2 border-dashed border-violet-400 bg-white/70 py-1.5 text-[10px] font-semibold text-violet-700 pointer-events-none"
                data-testid={`dv3-cr-evidence-drop-hint-${r.id}`}>
                <UploadCloud className="h-3 w-3" />{T("松开即上传到「第 {0} 轮 City 审查意见」（可多份）", [r.round])}
              </div>
            )}
            {evidenceFiles.length > 0 && (
              <ul className="mt-1 space-y-0.5 pl-4" data-testid={`dv3-cr-evidence-files-${r.id}`}>
                {evidenceFiles.map((f) => (
                  <li key={f.aid} className="flex items-center gap-1 text-[10px]"
                    data-testid={`dv3-cr-evidence-file-${f.aid}`} data-voided={f.voided ? "1" : "0"}>
                    <div className="min-w-0 flex-1 basis-[180px]">
                      <button type="button" onClick={() => onEvidencePreview?.(f)} title={T("页内预览")}
                        className={`block w-full truncate text-left font-semibold ${f.voided ? "text-slate-400 line-through" : "text-violet-700 hover:underline"}`}>
                        {f.name}
                      </button>
                      <FileUploadDate value={f.uploadedAt} fallbackValue={f.at} className="block" />
                    </div>
                    <span className="shrink-0 text-slate-400">{fmtMB(f.size)}</span>
                    <button type="button" title={T("下载")} onClick={() => downloadFile(f.fileId, f.name).catch((e) => toast.error(e?.message || T("下载失败")))}
                      className="shrink-0 text-slate-400 hover:text-violet-700"><Download className="h-2.5 w-2.5" /></button>
                    {meta.canEdit && !f.voided && (
                      <button type="button" title={T("作废（保留留档）")} data-testid={`dv3-cr-evidence-void-${f.aid}`}
                        onClick={() => onEvidenceVoid?.(f)} disabled={evidenceBusy === `${f.aid}-void`}
                        className="shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-50"><XCircle className="h-2.5 w-2.5" /></button>
                    )}
                    {f.voided && <span className="shrink-0 text-red-500" title={f.voidReason || ""}>{T("已作废")}</span>}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-0.5 pl-[18px] text-[9px] text-slate-400">{T("City 退回意见 / plan check 结果单；每轮各存一处，互不覆盖。")}</p>
          </div>
            )}
          </EvidenceDropArea>
          {/* 顺延 / 期限 / 责任人：轮次的「谁该做什么」信息，放在材料窗口之前，
              让人先知道这轮归谁、顺延几天，再去看要交什么。 */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-500">
            <span data-testid={`dv3-cr-delay-${r.id}`} className="inline-flex items-center gap-0.5 font-semibold text-amber-700">
              <Clock className="h-2.5 w-2.5" />{r.retrospective
                ? T("历史补录 · 不回退阶段、不改变项目工期")
                : T("本轮已把项目整体顺延 {0} 天", [r.delayDays || 0])}
            </span>
            {r.cityDueAt && <span>{T("City 回复期限 {0}", [r.cityDueAt])}</span>}
            <span>{T("图纸 {0}", [who("drawing")])}</span>
            <span>{T("· 回复函 {0}", [who("response_letter")])}</span>
          </div>
          <div className="grid gap-1.5 sm:grid-cols-2" data-testid={`dv3-cr-slots-${r.id}`}>
            {["drawing", "response_letter"].map((slot) => (
              <Slot key={slot} r={r} slot={slot} meta={meta} busy={busy}
                onUpload={onUpload} onVoid={onVoid} onPreview={onPreview} />
            ))}
          </div>
          {r.closedAt ? (
            <p className="flex items-center gap-1 text-[10px] text-sky-700" data-testid={`dv3-cr-closed-${r.id}`}>
              <CheckCircle2 className="h-3 w-3" />{r.retrospective
                ? T("{0} 已归档历史整改记录", [r.closedBy])
                : T("{0} 标记完成并重新提交 City", [r.closedBy])}{r.closeNote ? `：${r.closeNote}` : ""} · {fmtDT(r.closedAt)}
            </p>
          ) : r.voidedAt ? (
            /* 已作废的误开轮次（2026-10-27）：保留在历史里可追溯，但不再是待办。
               不显示「还缺什么」也不再给任何上传 / 完成入口 —— 它从未产生材料。 */
            <p className="flex flex-wrap items-center gap-1 text-[10px] text-slate-500" data-testid={`dv3-cr-voided-${r.id}`}>
              <Ban className="h-3 w-3 shrink-0 text-red-500" />
              {T("{0} 作废了本轮（未产生任何材料）", [r.voidedBy])}
              {r.voidReason ? <span className="text-slate-400">：{r.voidReason}</span> : null}
              <span className="text-slate-400"> · {fmtDT(r.voidedAt)}</span>
            </p>
          ) : (
            <div className="flex items-center gap-1.5">
              <button type="button" disabled={!meta.canEdit || !r.complete || busy === r.id}
                data-testid={`dv3-cr-close-${r.id}`} onClick={() => onClose(r)}
                title={r.complete ? T("图纸与回复函已交齐，标记本轮完成并重新提交 City") : T("两类材料都交齐后才能标记完成")}
                className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white hover:bg-slate-700 disabled:opacity-40">
                {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}{r.retrospective ? T("标记完成 · 归档历史补录") : T("标记完成 · 已重新提交 City")}
              </button>
              {/* 作废入口只在「一份材料都没有」时出现（2026-10-27）：
                  传过东西的轮次有审计价值，该走「标记完成」而不是作废，后端也会拒。
                  放在这里是为了让误开轮次有正常出口 —— 否则它会永久卡住本阶段的补录入口。 */}
              {!r.complete && (r.files || []).length === 0 && (r.allFiles || 0) === 0 && (
                <button type="button" disabled={!meta.canEdit || busy === `${r.id}-void`}
                  data-testid={`dv3-cr-void-round-${r.id}`} onClick={() => onVoidRound(r)}
                  title={T("本轮开错了、一份材料都没传过：作废后不再计入待补齐，但记录与已生效的工期顺延都会保留")}
                  className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-[10px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">
                  {busy === `${r.id}-void` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}{T("作废本轮")}
                </button>
              )}
              {!r.complete && <span className="text-[10px] text-red-600">{T("还缺：{0}", [(r.missing || []).map((s) => SLOT_META[s]?.label || s).join("、")])}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 主区块：City 审查结果。
 *
 * @param {string} pid      项目 id
 * @param {string} stageKey planning_review | building_review
 * @param {object} meta     设计部权限（canEdit / isManager）
 * @param {object} api      axios 实例
 * @param {function} onChanged 任何写操作成功后通知父级刷新（阶段状态 / 截止日都变了）
 *
 * 为什么自己拉一次数据而不是从父级 props 传下来：
 *   父亲（LegacyTabs 的 renderStage）走的是 /nodes 聚合接口，那个接口为了列表性能
 *   只带阶段级字段；把轮次塞进去会让每个项目详情都多查一次轮次集合。
 *   这里按阶段懒加载：只有展开了这两个阶段才请求。
 */
export const CityReviewBlock = ({ pid, stageKey, meta, api, onChanged }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [cityDue, setCityDue] = useState("");
  const [lb, setLb] = useState(null);
  // 灯箱条目与「这次点的是哪一轮」绑定：换一轮后再点，必须显示新一轮的文件，
  // 否则会出现「标题写着第 2 轮、预览的是第 1 轮图纸」这种错位。
  const [lbItems, setLbItems] = useState([]);
  /* City 审查结论附件（2026-10-21 用户需求「通过也需要附件、未通过也需要附件」）。
     单独一份 state：它既不属于某一轮整改，也不是阶段字段，而是这个阶段的对外凭证。
     通过 / 未通过两个按钮的可点状态都看 evidenceCount > 0，规则只在这里表达一次。 */
  /* 结论附件（City 给我们）—— 按轮次分组（2026-10-27）。
     files 是全量（含已归轮 + 未归轮），byRound / unassigned 是服务端分好的两段视图。
     count 是**全部未作废总数**，也是「通过 / 未通过」门禁的依据：
     凭证属于「本阶段」而不是「某一轮」，改成按轮计数会让挂了轮次的老文件解锁不了按钮。 */
  const [evidence, setEvidence] = useState({ files: [], byRound: {}, unassigned: [], count: 0 });
  const [evBusy, setEvBusy] = useState(null);
  const evInp = useRef(null);

  /* 服务端分桶缺失时就地补算（2026-10-27 防回归）。
     为什么需要：byRound / unassigned 是这次新加的字段，只要出现「新前端 + 旧响应」的组合
     （灰度滚动发布时命中还没升级的后端实例、浏览器缓存了改造前的响应、单测 mock 只给 files），
     阶段级清单会一条都不渲染，反而显示「另有 N 份已归入各自轮次」——
     用户看到的结果是「我那 9 份 City 意见全都不见了」，比不显示分组更糟。
     这里按 round 字段重新分一次桶，口径与服务端逐字一致（round 是 >=1 的整数才算已归轮），
     保证任何响应形态下都不会漏掉未删除的文件。 */
  const normalizeEvidence = (ev) => {
    const base = ev || {};
    const files = Array.isArray(base.files) ? base.files : [];
    const ok = base.byRound && typeof base.byRound === "object" && Array.isArray(base.unassigned);
    const byRound = {};
    const unassigned = [];
    if (!ok) {
      files.forEach((f) => {
        const r = f?.round;
        if (Number.isInteger(r) && r >= 1) {
          const k = String(r);
          byRound[k] = [...(byRound[k] || []), f];
        } else {
          unassigned.push(f);
        }
      });
    }
    return {
      ...base,
      files,
      byRound: ok ? base.byRound : byRound,
      unassigned: ok ? base.unassigned : unassigned,
      // count 缺省时按「未作废总数」兜底，与后端门禁口径一致 —— 不能让按钮因为少了字段就全灰。
      count: typeof base.count === "number" ? base.count : files.filter((f) => !f.voided).length,
    };
  };

  const load = useCallback(() => {
    setErr(null);
    return Promise.all([
      api.get(`/design/v3/projects/${pid}/corrections/${stageKey}`),
      // 结论附件失败不能连坐整个块：轮次历史仍要看得到，只是暂时不能判定结论。
      api.get(`/design/v3/projects/${pid}/corrections/${stageKey}/result-evidence`)
        .then(({ data }) => normalizeEvidence(data)).catch(() => ({ files: [], byRound: {}, unassigned: [], count: 0 })),
    ])
      .then(([{ data }, ev]) => { setD(data); setEvidence(ev); })
      .catch((e) => setErr(e?.detailText || e.message));
  }, [api, pid, stageKey]);
  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); onChanged?.(); };
  const rounds = d?.rounds || [];
  const openRound = (d?.openRound) || null;
  const delayDays = d?.cfg?.delayDays ?? 3;
  /* 找不到宿主轮次的附件要退回阶段级清单显示（2026-10-27 防回归）。
     已归轮的文件只在该轮卡内渲染，所以「文件的 round 在 rounds 里找不到」就意味着一份
     City 意见两处都不显示 —— 用户会认为文件被系统吃掉了。已知会发生的场景：
     轮次文档被归档 / 数据不一致 / 归轮与轮次列表读到了不同快照。
     这里把它们并回 unassigned（界面上仍带「归到轮次…」下拉，可重新归到有效轮次），
     宁可多显示一行，也不能让已有凭证消失。 */
  const roundNos = new Set((d?.rounds || []).map((x) => String(x.round)));
  const unassignedView = (() => {
    const base = evidence.unassigned || [];
    const orphan = Object.entries(evidence.byRound || {})
      .filter(([k]) => !roundNos.has(String(k)))
      .flatMap(([, v]) => v || []);
    return orphan.length ? [...orphan, ...base] : base;
  })();
  /* 阶段级「已归入轮次」的计数也要扣掉上面这些孤儿，否则同一份文件既列在清单里
     又被算进「另有 N 份在轮次卡内」，看起来像传了两份。 */
  const assignedCount = Math.max(0, (evidence.files || []).length - unassignedView.length);
  /* P+B 合并模式（2026-10-22 用户口径「p+b 模式他们就是一个图了..改的话就全改」）。
     concurrent 项目里 Planning / Building Review 是同一套图的两次报批审查，
     City 退回是两边一起改，所以后端把轮次 / 结论附件都按组合并，前端这一块
     只渲染一次（由详情页的合并节点容器决定挂在哪），标题与按钮文案随之调整。 */
  const merged = !!d?.merged;
  const stageKeys = d?.stageKeys || [stageKey];
  /* 阶段顺序锁（2026-10-22 用户需求「整个流程现在需要按整个流程推进」）：
     City 块的「已通过 · 进入下一步」会走 stage_close_core 关阶段，
     「未通过 · 新增整改轮次」会立刻顺延项目工期 —— 两个动作都属于「本阶段的工作」，
     上一阶段没做完就不该能点。后端两处都会用同一个判定拦成 400，
     这里把它下发的 stageLock 用来**提前置灰**，避免「按钮亮着、点了才报错」。
     只锁这两个断定结论的按钮：轮次历史、结论附件上传照常可用 ——
     那些是记录既成事实的材料，不会推进流程、也不会改工期。 */
  const stageLock = d?.stageLock || { allowed: true };
  const lockOn = stageLock.allowed === false;
  /* 文案说明「流程现在停在哪一步」而不是「上一阶段没做完」：
     判定看的是当前阶段，说成「上一阶段」会把历史未补 / 并行的阶段说反。 */
  const lockText = lockOn
    ? T("流程还没走到本阶段（当前停在「{0}」）", [stageLock.currentName || stageLock.currentKey || ""])
    : "";

  /* 新增一轮：City 退回。必填原因（对应 City 意见），否则事后无法回答「这一轮在改什么」。 */
  const addRound = async () => {
    const txt = reason.trim();
    if (txt.length < 4) return toast.error(T("请填写 City 退回意见（至少 4 个字）"));
    const historical = !!d?.passed;
    setBusy("add");
    try {
      const { data } = await api.post(`/design/v3/projects/${pid}/corrections/${stageKey}`,
        { reason: txt, cityDueAt: cityDue || undefined, historical });
      if (data.round?.retrospective || data.shift?.historical) {
        toast.success(T("已补录第 {0} 轮历史整改，不回退阶段、不改变项目工期", [
          data.round?.round ?? "",
        ]));
      } else {
        toast.success(T("已新增第 {0} 轮整改，项目整体顺延 {1} 天{2}", [
          data.round?.round ?? "",
          data.shift?.days ?? delayDays,
          data.shift?.targetTo ? T("（目标完成日 → {0}）", [data.shift.targetTo]) : "",
        ]));
      }
      setReason(""); setCityDue(""); setAddOpen(false);
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  /* 往某一轮上传材料。**一次可以传多份**（2026-10-23：拖拽支持多选，
     点选也同步改成 multiple，两个入口结果必须一致）。

     为什么前端串行逐个调接口，而不是一次塞多个文件：
     后端 /corrections/{rid}/upload 是「一个请求一份材料」的口径（slot 是请求级参数，
     每份文件要各自生成 aid / 活动记录 / 审计）。串行还带来一个好处 ——
     中途某一份失败时，前面成功的已经在服务端落库，提示里如实报「成功几份、失败几份」即可，
     不会出现「全成 / 全败」的假象。
     失败不中断后续文件：用户拖 5 份图纸，不能因为第 2 份格式不对就悄悄丢掉后 3 份。 */
  const uploadFile = async (r, slot, picked) => {
    const list = Array.from(picked || []).filter(Boolean);
    if (!list.length) return;
    setBusy(`${r.id}-${slot}`);
    let done = 0;
    let lastRound = null;   // 服务端每次返回的最新轮次视图，用来在提示里说「材料已齐」
    const failed = [];
    for (const file of list) {
      if (file.size > MAX_UPLOAD_BYTES) {
        failed.push(`${file.name}（${T("超过 {0}MB", [MAX_UPLOAD_MB])}）`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("slot", slot);
        fd.append("file", file);
        const { data } = await api.post(`/design/v3/corrections/${r.id}/upload`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        lastRound = data?.round || null;
        done += 1;
      } catch (e) {
        failed.push(`${file.name}（${errTextOf(e?.response?.data?.detail || e.message)}）`);
      }
    }
    if (done) {
      // 只剩一份时沿用原来的逐文件名提示；多份时给总数，完整交齐再把「已齐」这句带上。
      const base = list.length === 1
        ? T("已上传 {0}", [list[0].name])
        : T("已上传 {0} 份材料", [done]);
      toast.success(lastRound?.complete ? `${base} · ${T("本类材料已齐")}` : base);
    }
    if (failed.length) {
      toast.error(T("有 {0} 份没传上去：{1}", [failed.length, failed.join("；")]));
    }
    if (done) changed();
    setBusy(null);
  };

  const voidFile = async (r, f) => {
    const why = (window.prompt(T("作废「{0}」的原因（会保留留档）", [f.name]), "") || "").trim();
    if (!why) return;
    setBusy(`${r.id}-void`);
    try {
      await api.post(`/design/v3/corrections/${r.id}/void`, { aid: f.aid, reason: why });
      toast.success(T("已作废，历史仍留档"));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const closeRound = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/design/v3/corrections/${r.id}/close`, {});
      toast.success(r.retrospective
        ? T("第 {0} 轮历史整改记录已归档", [r.round])
        : T("第 {0} 轮已标记完成，已重新提交 City", [r.round]));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  /* 作废一轮「误开的空轮次」（2026-10-27）。
     为什么需要：误开的轮次原来没有任何出口 —— 「标记完成」要求图纸 + 回复函都交齐，
     「新增下一轮」又拦「上一轮还没结束」，于是一条 0 材料的轮次会永久卡住，
     还把已关闭阶段的「补录历史整改轮次」一起锁死（disabled={!!openRound}）。
     生产实例：306 Jackson St 的 planning 第 1 轮。
     为什么必填原因：作废等于宣告「这一轮从未发生过工作」，事后必须能回答「凭什么这么说」。
     为什么提示里写明「不顺延回滚」：开轮时项目已整体 +N 天，那是已对外的交期，
     作废只改轮次状态、不碰日期 —— 不说清楚会让人以为作废能把交期收回来。
     为什么不用 window.prompt 收原因（2026-10-27）：本项目已经踩过一次 ——
     不支持原生弹窗的宿主（ZCode 内置浏览器 IAB 等）会直接抛 `prompt() is not supported.`，
     页面糊一层红色报错且用户不知道那步到底生效没有（见 DesignConfirmDialog 头注释）。
     所以改成走项目既有的 DesignConfirmDialog：先弹确认框（把「日期不回滚」写在警告里），
     确认后才真正提交。 */
  const [voidTarget, setVoidTarget] = useState(null);
  const voidRound = (r) => setVoidTarget(r);
  const submitVoidRound = async (why) => {
    const r = voidTarget;
    if (!r) return;
    if (!why) return;
    if (why.length < 4) return toast.error(T("请把原因写清楚（至少 4 个字）"));
    setBusy(`${r.id}-void`);
    try {
      const { data } = await api.post(`/design/v3/corrections/${r.id}/void-round`, { reason: why });
      const kept = Number(data?.delayDaysKept || 0);
      toast.success(kept > 0
        ? T("第 {0} 轮已作废。注意：本轮开轮时顺延的 {1} 天仍然生效，没有回滚", [r.round, kept])
        : T("第 {0} 轮已作废", [r.round]));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
    setVoidTarget(null);
  };

  /* ===== City 审查结论附件（2026-10-21 用户需求） =====
     用户原话：「两个审流程 现在 审未通过需要附件...通过也需要附件」。
     所以通过 / 未通过并不是随手一点的判定，而是必须先把 City 的书面结论传上来：
       · 通过   → 传批准函 / plan check 通过单；
       · 未通过 → 传 plan check 意见单 / City 退回邮件。
     两份都走同一个上传入口（本阶段一份或多份），后端在 pass 与新增轮次时各校验一次。
     这里不在前端预判「该传哪种」：City 给的材料格式不固定，硬编只会造成来回改名。 */
  /* 上传结论附件。与轮次材料同一口径：可一次多份，串行逐个传，失败不中断后续，
     最后如实报「成功几份 / 哪几份没上去」。
     （2026-10-23 用户需求「这个位置请允许可以拖拽文件上传」：City 常常一次给一封邮件带多个附件，
     一次拖进来全传完，比反复点「上传」快，也不容易漏。）

     2026-10-27：新增 round 参数。传了轮次 → 文件挂到该轮整改卡的上传窗；
     不传 → 落在阶段级窗口（未归轮：初次意见 / 批准函 / 存量文件）。
     两条路径共用这一份实现，差别只在 form 里带不带 round，避免两套逻辑漂移。 */
  const uploadEvidence = async (picked, rnd = null) => {
    const list = Array.from(picked || []).filter(Boolean);
    if (!list.length) return;
    // 轮次上传与阶段级上传各自的 loading key 不能相同，否则给第 2 轮传文件时
    // 第 1 轮和阶段级按钮会一起转圈（用户会以为同时传了三处）。
    const busyKey = rnd ? `ev-round-${rnd}` : "upload";
    setEvBusy(busyKey);
    let done = 0;
    const failed = [];
    for (const file of list) {
      if (file.size > MAX_UPLOAD_BYTES) {
        failed.push(`${file.name}（${T("超过 {0}MB", [MAX_UPLOAD_MB])}）`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("note", "");
        // 不指定轮次时显式传空串：后端按「未归轮」处理（round 用 str 接收，空串不报 422）。
        fd.append("round", rnd == null ? "" : String(rnd));
        const { data } = await api.post(
          `/design/v3/projects/${pid}/corrections/${stageKey}/result-evidence`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        // 逐份写回，保证「传完第 1 份就可见」；count 以服务端最近一次返回为准。
        // 2026-10-27：顺手把这一份插进对应分组（按轮 or 未归轮），否则要等整页重载
        // 才在那轮的窗口里看到自己刚传的文件 —— 用户会以为传丢了并重复上传。
        setEvidence((cur) => {
          // 响应里没有 file（老版本后端 / 异常降级）时不做乐观插入，也不塞 undefined 进列表：
          // 只更新 count 会让列表里多一条没有 aid 的幽灵行，后续 map 出 key=undefined 直接报错。
          // 这种情况交给随后的 changed() 整份重取，以服务端为准。
          const entry = data?.file;
          if (!entry || !entry.aid) return { ...cur, count: typeof data?.count === "number" ? data.count : cur.count };
          const bucket = entry.round;
          const key = bucket == null ? null : String(bucket);
          const next = { ...cur, files: [entry, ...(cur.files || [])], count: data.count };
          if (key == null) {
            next.unassigned = [entry, ...(cur.unassigned || [])];
          } else {
            next.byRound = { ...(cur.byRound || {}), [key]: [entry, ...((cur.byRound || {})[key] || [])] };
          }
          return next;
        });
        done += 1;
      } catch (e) {
        failed.push(`${file.name}（${errTextOf(e?.response?.data?.detail || e.message)}）`);
      }
    }
    if (done) {
      const where = rnd == null ? "" : T("（第 {0} 轮）", [rnd]);
      toast.success(list.length === 1
        ? T("已上传 City 审查结论附件{0}：{1}", [where, list[0].name])
        : T("已上传 {0} 份 City 审查结论附件{1}", [done, where]));
    }
    if (failed.length) {
      toast.error(T("有 {0} 份没传上去：{1}", [failed.length, failed.join("；")]));
    }
    if (done) changed();
    setEvBusy(null);
  };

  /* 把存量附件（改造前上传、没有轮次标记）归到某一轮。
     2026-10-27：自动按时间推断会猜错 —— City 邮件到达时间和我们的开轮 / 关轮时间本来
     就不重合（意见可能晚几天才传），错挂比不挂更糟。所以做成人工一次性归轮，
     挂在阶段级列表的每一行上。 */
  const assignRound = async (f, rnd) => {
    setEvBusy(`${f.aid}-assign`);
    try {
      await api.post(`/design/v3/projects/${pid}/corrections/${stageKey}/result-evidence/assign-round`,
        { aids: [f.aid], round: rnd });
      toast.success(rnd ? T("已归到第 {0} 轮", [rnd]) : T("已退回「未归轮」"));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setEvBusy(null);
  };

  const voidEvidence = async (f) => {
    const why = (window.prompt(T("作废「{0}」的原因（会保留留档）", [f.name]), "") || "").trim();
    if (!why) return;
    setEvBusy(`${f.aid}-void`);
    try {
      const { data } = await api.post(
        `/design/v3/projects/${pid}/corrections/${stageKey}/result-evidence/void`,
        { aid: f.aid, reason: why });
      toast.success(T("已作废，历史仍留档"));
      // 作废后整份清单重取而不是本地打标记：服务端返回的 count 是门禁依据，
      // 而文件可能挂在组内另一阶段文档上（P+B 合并），本地改一条会漏掉其它文档。
      load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setEvBusy(null);
  };

  /* 结论附件预览：并入轮次同一套灯箱，靠 items 区分，避免再开一个灯箱实例。 */
  /* 结论附件预览：并入轮次同一套灯箱，靠 items 区分，避免再开一个灯箱实例。
     2026-10-27：条目按**当前窗口**（某一轮 or 未归轮）过滤，不跨窗聚合 ——
     否则在第 2 轮窗口里前后翻页会翻到第 1 轮的文件，正是「标题写第 2 轮、内容却是第 1 轮」那类错位。 */
  const previewEvidence = (f, list) => {
    const scope = list && list.length ? list : (evidence.files || []);
    const items = scope.filter((x) => !x.voided && x.fileId).map((x) => ({
      id: x.aid, fileId: x.fileId, size: x.size,
      url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${x.fileId}/download`,
      kind: mediaKind(x.name, x.contentType),
      name: x.name,
      meta: `${T("City 审查结论")} · ${fmtMB(x.size)} · ${x.uploader} · ${uploadDateLabel(x.uploadedAt, x.at)}`,
      caption: x.note || "",
    }));
    const idx = items.findIndex((x) => x.id === f.aid);
    setLbItems(items);
    setLb(idx >= 0 ? idx : 0);
  };

  /* 「通过」= 关闭阶段并进入下一阶段。走 design_module 的同一份关阶段逻辑，
     所以文件门禁 / 产物审核门禁 / 付款触发都会照常执行；被门禁挡住时把后端原因原样带出来。 */
  const passStage = async () => {
    if (!window.confirm(T("确认 City 已批准，关闭「{0}」并进入下一阶段？\n\n会照常校验阶段门禁（必需文件 / 产物审核）；未通过时会被拦下并提示原因。", [d?.stageName || stageKey]))) return;
    setBusy("pass");
    try {
      await api.post(`/design/v3/projects/${pid}/corrections/${stageKey}/pass`, {});
      toast.success(T("City 审查通过 · 阶段已关闭，进入下一阶段"));
      changed();
    } catch (e) {
      const det = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
      if (det && typeof det === "object" && det.error === "stage_order_violation") {
        // 流程顺序门禁（2026-10-22）：按钮已按 stageLock 置灰，这里兜住「页面数据过期 /
        // 直接调接口」的情况。不做 override —— 越级关阶段会让整条流程错位。
        toast.error(det.message || T("流程还没走到本阶段，请先完成当前阶段"));
      } else if (det && typeof det === "object" && det.error === "gate_files_missing") {
        toast.error(T("阶段门禁未通过：{0}", [(det.detail || []).map((x) => `${x.task}（缺 ${(x.missing || []).join("、")}）`).join("；")]));
      } else if (det && typeof det === "object" && det.error === "stage_products_not_approved") {
        toast.error(T("还有产物未通过审核：{0}", [(det.detail || []).map((x) => x.name || x.key).join("、")]));
      } else {
        toast.error(typeof det === "string" ? det : (det?.message || e.message));
      }
    }
    setBusy(null);
  };

  const preview = (r, f) => {
    const items = (r.files || []).filter((x) => !x.voided).map((x) => ({
      id: x.aid, fileId: x.fileId, size: x.size,
      url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${x.fileId}/download`,
      kind: mediaKind(x.name, x.contentType),
      name: x.name,
      meta: `${SLOT_META[x.slot]?.label || x.slot} · ${fmtMB(x.size)} · ${x.uploader} · ${uploadDateLabel(x.uploadedAt, x.at)}`,
      caption: x.note || "",
    }));
    const idx = items.findIndex((x) => x.id === f.aid);
    setLbItems(items);
    setLb(idx >= 0 ? idx : 0);
  };

  /* 结论附件投放区（2026-10-23 用户需求「这个位置请允许可以拖拽文件上传」）。
     是否可投放与「上传」按钮同一口径：有编辑权限且本阶段还没判通过。
     注意这里必须**无条件**调用 hook（上面有 err / loading 的提前 return），
     否则 hooks 顺序会在渲染之间跳变。 */
  const evidenceDrop = useFileDrop({
    // 已关闭阶段仍允许补传 City 结论附件：历史补录需要先有结论凭证，
    // 但上传它本身不会回退阶段或改变工期。
    disabled: !meta.canEdit || evBusy === "upload",
    onFiles: (list) => uploadEvidence(list),
  });

  if (err) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50/50 px-3 py-2 text-[11px] text-red-700" data-testid={`dv3-city-review-error-${stageKey}`}>
        <AlertTriangle className="mr-1 inline h-3 w-3" />{errTextOf(err)}
      </div>
    );
  }
  if (!d) {
    return <div className="py-2 text-center text-slate-400" data-testid={`dv3-city-review-loading-${stageKey}`}><Loader2 className="h-3.5 w-3.5 animate-spin inline" /></div>;
  }

  /* 阶段已通过：块保留结论与全部历史轮次，同时开放“补录历史整改轮次”。
     为什么不直接隐藏：整改轮次是项目的审批历史，City 退过几次是复盘与责任追溯的依据。 */
  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50/30 p-2.5 space-y-2" data-testid={`dv3-city-review-${stageKey}`}
      data-passed={d.passed ? "1" : "0"} data-rounds={rounds.length} data-open={openRound ? "1" : "0"}
      data-merged={merged ? "1" : "0"}
      /* 兜底：文件拖到块内空白处（不是任何投放区）时也拦住浏览器默认的「打开该文件」，
         否则用户拖偏一点，当前正在看的项目页会被一个 PDF 直接顶掉。 */
      onDragOver={blockFileDrop} onDrop={blockFileDrop}>
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-orange-600" />
        <span className="text-[11px] font-bold uppercase tracking-wide text-orange-800">{T("City 审查结果（整改轮次）")}</span>
        {/* P+B 合并标识（2026-10-22 用户口径「p+b 模式他们就是一个图了..改的话就改全改」）：
            concurrent 项目里这一块同时代表 Planning Review 与 Building Review，
            City 退一次图两边一起改，所以标出来，避免用户以为还漏了另一个阶段的块。 */}
        {merged && (
          <span className="rounded-full bg-teal-600 px-1.5 py-0.5 text-[9px] font-bold text-white"
            data-testid={`dv3-city-review-merged-tag-${stageKey}`}>{T("P+B 合并")}</span>
        )}
        <span className="text-[10px] text-slate-500" data-testid={`dv3-city-review-count-${stageKey}`}>
          {rounds.length ? T("{0} 轮整改", [rounds.length]) : T("暂无整改")}
          {openRound ? T(" · 第 {0} 轮待补齐", [openRound.round]) : ""}
        </span>
        {/* 合并模式下逐阶段通过情况：P 批了 B 还在审是常态，直接写出来，
            否则「未通过」按钮可用、上面却显示已通过，看起来自相矛盾。 */}
        {merged && (d.passedByStage || []).length > 0 && (
          <span className="flex flex-wrap items-center gap-1" data-testid={`dv3-city-review-bystage-${stageKey}`}>
            {(d.passedByStage || []).map((x) => (
              <span key={x.key}
                className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${x.passed ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                data-testid={`dv3-city-review-bystage-${x.key}`} data-passed={x.passed ? "1" : "0"}>
                {x.key === "planning_review" ? "P" : x.key === "building_review" ? "B" : x.key}
                {x.passed ? " ✓" : ""}
              </span>
            ))}
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400">{d.passed
          ? T("历史补录不会回退阶段 / 不改变项目工期")
          : T("每新增一轮 → 项目整体顺延 {0} 天", [delayDays])}</span>
      </div>

      {/* City 审查结论附件（2026-10-21 用户需求「通过也需要附件、未通过也需要附件」）：
          放在「审查结果是什么」这一段的上方，因为它是给出结论的前提 ——
          没有凭证时两个按钮都会是禁用的，先把要传的东西摆出来，用户不会先点再被拦。
          已通过（阶段已关闭）时仍保留这份凭证，它是这个阶段唯一的对外结论留档。
          2026-10-23：整区支持拖拽投放（用户需求「这个位置请允许可以拖拽文件上传」）。

          2026-10-27：这一块收窄为「未归轮的阶段级文件」——
            · 初次送审的 City 意见、批准函：它们不属于任何一轮整改，只能放在这里；
            · 改造前的存量文件（没有 round 字段）：不能让它们在界面上消失（历史证据红线），
              在这里显示并支持人工归轮。
          按轮的 City 意见改到每条轮次卡里各自上传（见 RoundCard）。 */}
      <div className={`rounded-md border px-2 py-1.5 transition-colors ${evidenceDrop.dragging ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200" : "border-slate-200 bg-white"}`}
        data-testid={`dv3-city-review-evidence-${stageKey}`} data-count={evidence.count || 0}
        data-unassigned={unassignedView.length}
        data-have={evidence.count ? "1" : "0"} data-dragging={evidenceDrop.dragging ? "1" : "0"}
        {...evidenceDrop.props}>
        <div className="flex flex-wrap items-center gap-1.5">
          <FileCheck2 className={`h-3 w-3 shrink-0 ${evidence.count ? "text-slate-400" : "text-red-500"}`} />
          <span className={`text-[11px] font-semibold ${evidence.count ? "text-slate-700" : "text-red-600"}`}>
            {T("City 审查结论附件（阶段级）")}
          </span>
          {!evidence.count
            ? <span className="text-[10px] font-semibold text-red-500" data-testid={`dv3-city-review-evidence-missing-${stageKey}`}>{T("未上传 · 通过 / 未通过都需先上传")}</span>
            : <span className="text-[10px] text-slate-400" data-testid={`dv3-city-review-evidence-count-${stageKey}`}>{T("共 {0} 份", [evidence.count])}</span>}
          {meta.canEdit && (
            <>
              <button type="button" onClick={() => evInp.current?.click()} disabled={evBusy === "upload"}
                title={T("上传阶段级 City 结论附件：初次送审意见 / 批准函（也可直接把文件拖到本区域）；某一轮的退回意见请到该轮卡里上传")}
                data-testid={`dv3-city-review-evidence-upload-${stageKey}`}
                className="ml-auto inline-flex items-center gap-0.5 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 disabled:opacity-50">
                {evBusy === "upload" ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <FileUp className="h-2.5 w-2.5" />}{T("上传")}
              </button>
              {/* multiple：拖拽能一次拖多份，点选也要能一次选多份（City 常一封邮件带多个附件） */}
              <input ref={evInp} type="file" multiple className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
                data-testid={`dv3-city-review-evidence-input-${stageKey}`}
                onChange={(e) => { const list = Array.from(e.target.files || []); e.target.value = ""; if (list.length) uploadEvidence(list); }} />
            </>
          )}
        </div>
        {evidenceDrop.dragging && (
          <div className="mt-1 flex items-center justify-center gap-1 rounded border-2 border-dashed border-violet-400 bg-white/70 py-2 text-[10px] font-semibold text-violet-700 pointer-events-none"
            data-testid={`dv3-city-review-evidence-drop-hint-${stageKey}`}>
            <UploadCloud className="h-3 w-3" />
            {evBusy === "upload" ? T("上传中，请稍候…") : T("松开即上传 City 审查结论附件（可多份）")}
          </div>
        )}
        <p className="mt-0.5 pl-[18px] text-[9px] text-slate-400">
          {T("plan check 结果单 / 批准函 / 初次送审意见；通过或未通过都要先有这份凭证。")}
          {meta.canEdit && <span className="text-violet-500">{T(" · 可直接把文件拖到本区域")}</span>}
        </p>
        {/* 未归轮清单（2026-10-27）：这里**只列没有轮次标记的文件**，已归轮的在各自轮次卡里。
            每行右侧给一个「归到第 N 轮」下拉 —— 改造前的存量文件只能靠人工归轮，
            自动按时间猜会挂错轮（City 邮件到达时间与开轮 / 关轮时间本来就不重合）。 */}
        {unassignedView.length > 0 && (
          <ul className="mt-1 space-y-0.5 pl-4" data-testid={`dv3-city-review-evidence-files-${stageKey}`}>
            {unassignedView.map((f) => (
              <li key={f.aid} className="flex items-center gap-1 text-[10px]"
                data-testid={`dv3-city-review-evidence-file-${f.aid}`} data-voided={f.voided ? "1" : "0"}>
                <div className="min-w-0 flex-1 basis-[180px]">
                  <button type="button" onClick={() => previewEvidence(f, unassignedView)} title={T("页内预览")}
                    className={`block w-full truncate text-left font-semibold ${f.voided ? "text-slate-400 line-through" : "text-violet-700 hover:underline"}`}>
                    {f.name}
                  </button>
                  <FileUploadDate value={f.uploadedAt} fallbackValue={f.at} className="block" />
                </div>
                <span className="shrink-0 text-slate-400">{fmtMB(f.size)}</span>
                <button type="button" title={T("下载")} onClick={() => downloadFile(f.fileId, f.name).catch((e) => toast.error(e?.message || T("下载失败")))}
                  className="shrink-0 text-slate-400 hover:text-violet-700"><Download className="h-2.5 w-2.5" /></button>
                {/* 归轮下拉：轮数 > 0 且文件未作废时才出现（没有轮次可归时这个入口没有意义）。 */}
                {meta.canEdit && !f.voided && rounds.length > 0 && (
                  <select title={T("把这份文件归到某一轮整改（归轮后在那一轮卡内显示）")}
                    value="" disabled={evBusy === `${f.aid}-assign`}
                    data-testid={`dv3-city-review-evidence-assign-${f.aid}`}
                    onChange={(e) => { const v = e.target.value; if (v !== "") assignRound(f, Number(v)); }}
                    className="shrink-0 rounded border border-slate-200 bg-white px-1 py-0 text-[9px] text-slate-600 disabled:opacity-50">
                    <option value="">{T("归到轮次…")}</option>
                    {[...rounds].sort((a, b) => a.round - b.round).map((rr) => (
                      <option key={rr.id} value={rr.round}>{T("第 {0} 轮", [rr.round])}</option>
                    ))}
                  </select>
                )}
                {meta.canEdit && !f.voided && (
                  <button type="button" title={T("作废（保留留档）")} data-testid={`dv3-city-review-evidence-void-${f.aid}`}
                    onClick={() => voidEvidence(f)} disabled={evBusy === `${f.aid}-void`}
                    className="shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-50"><XCircle className="h-2.5 w-2.5" /></button>
                )}
                {f.voided && <span className="shrink-0 text-red-500" title={f.voidReason || ""}>{T("已作废")}</span>}
              </li>
            ))}
          </ul>
        )}
        {/* 已归轮的文件不在这里重复列：它们已经在各自轮次卡内，重复会让人以为文件传了两份。
            但给一行计数，避免「文件去哪了」的困惑。 */}
        {assignedCount > 0 && (
          <p className="mt-0.5 pl-[18px] text-[9px] text-slate-400" data-testid={`dv3-city-review-evidence-assigned-count-${stageKey}`}>
            {T("另有 {0} 份已归入各自的整改轮次，在下方对应轮次卡内查看。", [assignedCount])}
          </p>
        )}
      </div>

      {/* 用户要求的那个按钮：上一次提交是否通过。已通过时改成结论行，避免重复点击。 */}
      {d.passed ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-800" data-testid={`dv3-city-review-passed-${stageKey}`}>
          <span className="flex min-w-0 items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />{merged
              ? T("City 已批准 · P+B 两个审图阶段已一起关闭，进入下一节点")
              : T("City 已批准 · 本阶段已关闭并进入下一阶段")}
          </span>
          {meta.canEdit && (
            <button type="button" disabled={!!openRound || !evidence.count} onClick={() => setAddOpen((v) => !v)}
              data-testid={`dv3-city-review-backfill-${stageKey}`}
              title={!evidence.count
                ? T("请先上传 City 审查结论附件（退回意见 / plan check 结果单）")
                : openRound ? T("第 {0} 轮历史补录还没归档，先交齐材料并标记完成；如果这一轮是误开的空轮次，可在轮次卡里「作废本轮」", [openRound.round])
                : T("补录已经发生过的 City 整改轮次；不回退阶段、不改变项目工期")}
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-[10px] font-semibold text-amber-800 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40">
              <History className="h-3 w-3" />{T("补录历史整改轮次")}
            </button>
          )}
          {meta.canEdit && !evidence.count && (
            <span className="w-full text-[10px] font-semibold text-red-600">
              {T("补录前请先上传 City 审查结论附件")}
            </span>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 bg-white px-2 py-2 space-y-1.5" data-testid={`dv3-city-review-ask-${stageKey}`}>
          <p className="text-[11px] font-semibold text-slate-700">{T("上一次提交 City，审查结果是什么？")}</p>
          <div className="flex flex-wrap items-center gap-1.5">
            {meta.canEdit && (
              <button type="button" disabled={!!openRound || busy === "pass" || !evidence.count || lockOn} onClick={passStage}
                data-testid={`dv3-city-review-pass-${stageKey}`}
                data-locked={lockOn ? "1" : "0"}
                title={lockOn ? lockText
                  : !evidence.count
                  ? T("请先上传 City 审查结论附件（批准函 / plan check 通过单）")
                  : openRound ? T("第 {0} 轮整改还没交齐，先完成本轮再判定通过", [openRound.round])
                  : merged ? T("City 批准 → 同时关闭 Planning Review 与 Building Review，进入下一节点")
                  : T("City 批准 → 关闭本阶段并进入下一阶段")}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy === "pass" ? <Loader2 className="h-3 w-3 animate-spin" /> : lockOn ? <Lock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}{merged ? T("已通过 · 两个阶段一起过") : T("已通过 · 进入下一步")}
              </button>
            )}
            {meta.canEdit && (
              <button type="button" disabled={!!openRound || !evidence.count || lockOn} onClick={() => setAddOpen((v) => !v)}
                data-testid={`dv3-city-review-reject-${stageKey}`}
                data-locked={lockOn ? "1" : "0"}
                title={lockOn ? lockText
                  : !evidence.count
                  ? T("请先上传 City 审查结论附件（plan check 结果单 / 退回意见）")
                  : openRound ? T("第 {0} 轮整改还没完成，先交齐材料并标记完成", [openRound.round])
                  : merged ? T("City 退回 → 新增一轮整改（一套修改图 + 回复函覆盖 P+B，只顺延一次）")
                  : T("City 退回 → 新增一轮整改（图纸 + 回复函）")}
                className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed">
                {lockOn ? <Lock className="h-3 w-3" /> : <RotateCcw className="h-3 w-3" />}{merged ? T("未通过 · 新增整改轮次（P+B）") : T("未通过 · 新增整改轮次")}
              </button>
            )}
            {/* 锁定时把「卡在哪一步」写明白：只置灰不说原因，用户会以为是权限或 bug。
                用 amber 而不是 red —— 这不是出错，只是流程还没轮到本阶段。 */}
            {lockOn && meta.canEdit && (
              <span className="flex items-start gap-1 text-[10px] font-semibold text-amber-700" data-testid={`dv3-city-review-lock-${stageKey}`}>
                <Lock className="mt-px h-2.5 w-2.5 shrink-0" /><span className="min-w-0">{lockText}</span>
              </span>
            )}
            {!evidence.count && meta.canEdit && (
              <span className="text-[10px] font-semibold text-red-600" data-testid={`dv3-city-review-evidence-required-${stageKey}`}>
                {T("两个按钮都需先上传结论附件")}
              </span>
            )}
            {!!openRound && (
              <span className="text-[10px] font-semibold text-amber-700" data-testid={`dv3-city-review-blocked-${stageKey}`}>
                {T("第 {0} 轮整改进行中，交齐后先「标记完成」", [openRound.round])}
              </span>
            )}
            {!meta.canEdit && <span className="text-[10px] text-slate-400">{T("仅设计部成员可判定审查结果")}</span>}
          </div>
        </div>
      )}

      {addOpen && !openRound && (
        <div className="space-y-1.5 rounded-md border border-amber-200 bg-white px-2 py-2" data-testid={`dv3-city-review-addform-${stageKey}`} data-historical={d.passed ? "1" : "0"}>
          <label className="block text-[10px] font-semibold text-slate-600">
            {d.passed ? T("历史 City 退回意见（必填，会写进补录记录）") : T("City 退回意见（必填，会写进整改记录）")}
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid={`dv3-city-review-reason-${stageKey}`}
              placeholder={T("例：1. Provide Title 24 CF1R compliance forms…\n2. Show egress window sizes on sheet A-201…")}
              className="mt-0.5 w-full rounded-md border border-slate-200 px-2 py-1 text-[11px] font-normal leading-5" />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[10px] text-slate-600">{T("City 回复期限（可选）")}
              <input type="date" value={cityDue} onChange={(e) => setCityDue(e.target.value)}
                data-testid={`dv3-city-review-citydue-${stageKey}`}
                className="ml-1 rounded-md border border-slate-200 px-1.5 py-0.5 text-[11px]" />
            </label>
            {/* 【2026-10-30】整改块版面紧，用 compact 形态挂在期限输入框后面。 */}
            <UsDateHint compact testid={`dv3-city-review-citydue-tz-${stageKey}`} />
            <span className="text-[10px] font-semibold text-amber-700">
              {d.passed
                ? T("只补历史工作记录：不回退阶段、不改变截止日和项目工期，也不影响下一阶段")
                : merged
                  ? T("新增后：一套图覆盖 P+B，项目目标完成日与后续阶段统一 +{0} 天（只顺延一次）", [delayDays])
                  : T("新增后：项目目标完成日与后续阶段统一 +{0} 天", [delayDays])}
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              <button type="button" onClick={() => { setAddOpen(false); setReason(""); }} className="rounded border border-slate-200 px-2 py-1 text-[10px] text-slate-600">{T("取消")}</button>
              <button type="button" disabled={busy === "add"} onClick={addRound} data-testid={`dv3-city-review-addsubmit-${stageKey}`}
                className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
                {busy === "add" ? <Loader2 className="h-3 w-3 animate-spin" /> : d.passed ? <History className="h-3 w-3" /> : <RotateCcw className="h-3 w-3" />}{d.passed ? T("补录第 {0} 轮历史整改", [rounds.length + 1]) : T("新增第 {0} 轮整改", [rounds.length + 1])}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 历史轮次倒序：最新一轮在最上面，与项目动态 / 审图意见的排序一致 */}
      {rounds.length > 0 && (
        <div className="space-y-1.5" data-testid={`dv3-city-review-rounds-${stageKey}`}>
          {[...rounds].reverse().map((r) => (
            <RoundCard key={r.id} r={r} meta={meta} busy={busy}
              onUpload={uploadFile} onVoid={voidFile} onPreview={preview} onClose={closeRound}
              onVoidRound={voidRound}
              /* 本轮 City 审查意见附件（2026-10-27）：按轮次从服务端分好的 byRound 里取，
                 不在前端重新过滤 —— 分轮口径只能有一处，否则 P+B 合并后两边算法会漂移。 */
              evidenceFiles={evidence.byRound?.[String(r.round)] || []}
              /* 直接把原始 busy key 传下去，由卡内按 `ev-round-N` 精确比较：
                 之前在这里做字符串切片转换，一旦 key 格式变化就会静默失配
                 （按钮永远不转圈、也不禁用，用户会连点重复上传）。 */
              evidenceBusy={evBusy}
              onEvidenceUpload={(rr, list) => uploadEvidence(list, rr.round)}
              onEvidenceVoid={voidEvidence}
              onEvidencePreview={(f) => previewEvidence(f, evidence.byRound?.[String(r.round)] || [])} />
          ))}
        </div>
      )}
      {!rounds.length && !d.passed && (
        <p className="text-[10px] text-slate-400" data-testid={`dv3-city-review-empty-${stageKey}`}>
          {T("City 一次通过时直接点「已通过」；被退回时点「未通过」新增一轮，每轮需交修改图纸 + 回复函。")}
        </p>
      )}
      {/* 轮数 vs 窗口数自检行（2026-10-27 用户需求「Correction 轮数和上传文件窗口数应保持一致」）。
          直接把这个对应关系写在界面上：用户看一眼就能确认系统有没有漏窗口，
          不用自己数卡片、也不用来回问我们为什么某一轮没有上传入口。 */}
      {rounds.length > 0 && (
        <p className="text-[10px] text-slate-400" data-testid={`dv3-city-review-windows-${stageKey}`}>
          {T("整改 {0} 轮 · 每轮各有独立的 City 意见窗口（共 {1} 个）", [rounds.length, rounds.length])}
        </p>
      )}
      {lb !== null && lbItems[lb] && (
        <MediaLightbox tid="dv3-cr-lightbox" items={lbItems} index={lb} onIndex={setLb} onClose={() => setLb(null)} api={api} zh />
      )}
      {/* 作废空轮次确认（2026-10-27）：走项目统一的 DesignConfirmDialog，
          不用 window.prompt —— 不支持原生弹窗的宿主会直接抛错（见组件头注释）。
          警告里必须写死「日期不回滚」：这是用户最容易误解的一点，
          作废看起来像「把这一轮抹掉」，但开轮时已经 +N 天的交期是已对外承诺的，
          作废只改轮次状态，不会把目标完成日收回去。 */}
      {voidTarget && (
        <DesignConfirmDialog
          testId="dv3-cr-void-round"
          tone="danger"
          title={T("作废第 {0} 轮整改", [voidTarget.round])}
          subtitle={voidTarget.reason || ""}
          message={T("这一轮没有任何材料，作废后不再计入待补齐，也不再挡住「新增整改轮次」与「补录历史轮次」；记录本身保留，可追溯是谁、什么时候、为什么作废。")}
          warning={T("本轮开轮时顺延的工期不会回滚：项目目标完成日与后续阶段仍然保持已顺延的日期。")}
          confirmText={T("确认作废本轮")}
          withReason
          reasonRequired
          reasonLabel={T("作废原因（必填，至少 4 个字）")}
          reasonPlaceholder={T("例如：误点「未通过」开了一轮，实际 City 没有退回")}
          busy={busy === `${voidTarget.id}-void`}
          onCancel={() => { if (busy !== `${voidTarget.id}-void`) setVoidTarget(null); }}
          onConfirm={submitVoidRound}
        />
      )}
    </div>
  );
};

export default CityReviewBlock;
